import React from "react";
import { BuildingIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../shadcn/ui/card";
import { cn } from "../utils";

const SpaceStatisticsCard = ({ className, data = {}, loading = false }) => {
  const {
    totalSpaces = 0,
    availableSpaces = 0,
    bookedSpaces = 0,
    availabilityPercentage = 0,
    bookingRate = 0,
    occupiedSpaces = 0,
    occupancyRate = 0,
  } = data || {};

  if (loading) {
    return (
      <Card className={cn("border shadow-none", className)}>
        <CardHeader>
          <div className="animate-pulse">
            <div className="h-6 bg-slate-200 rounded w-40 mb-2"></div>
            <div className="h-4 bg-slate-200 rounded w-28"></div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-16 bg-slate-200 rounded"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const statCards = [
    {
      title: "Total Spaces",
      value: totalSpaces,
      icon: BuildingIcon,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      description: "Total available spaces",
    },
    {
      title: "Available",
      value: availableSpaces,
      percentage: availabilityPercentage,
      icon: CheckCircleIcon,
      color: "text-green-600",
      bgColor: "bg-green-50",
      description: `${availabilityPercentage}% available`,
    },
    {
      title: "Currently Booked",
      value: bookedSpaces,
      percentage: bookingRate,
      icon: ClockIcon,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      description: `${bookingRate}% booking rate`,
    },
    {
      title: "Occupied",
      value: occupiedSpaces,
      percentage: occupancyRate,
      icon: XCircleIcon,
      color: "text-red-600",
      bgColor: "bg-red-50",
      description: `${occupancyRate}% utilization`,
    },
  ];

  return (
    <Card className={cn("border shadow-none", className)}>
      <CardHeader>
        <CardTitle className="text-xl flex items-center gap-2">
          <BuildingIcon className="h-5 w-5" />
          Space Statistics
        </CardTitle>
        <CardDescription>Current space availability and utilization</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          {statCards.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className={cn("p-2 rounded-lg", stat.bgColor)}>
                    <IconComponent className={cn("h-4 w-4", stat.color)} />
                  </div>
                  {stat.percentage !== undefined && (
                    <span className="text-xs font-medium text-slate-500">{stat.percentage}%</span>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-sm font-medium text-slate-700">{stat.title}</p>
                  <p className="text-xs text-slate-500">{stat.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default SpaceStatisticsCard;
